/**
 * useUndoRedo - Generic undo/redo hook
 * 
 * Maintains a stack of past states with configurable max history.
 * Provides set, undo, redo operations and state flags.
 */

import { useState, useCallback } from 'react'

interface UseUndoRedoReturn<T> {
  /** Current state value */
  state: T
  /** Set new state and add to history */
  set: (newState: T | ((prev: T) => T)) => void
  /** Undo to previous state */
  undo: () => void
  /** Redo to next state */
  redo: () => void
  /** Can undo */
  canUndo: boolean
  /** Can redo */
  canRedo: boolean
  /** Reset history */
  reset: (newState: T) => void
}

/**
 * useUndoRedo hook
 * 
 * @param initialState - Initial state value
 * @param maxHistory - Maximum number of history entries (default: 10)
 * @returns Undo/redo state management
 */
export function useUndoRedo<T>(
  initialState: T,
  maxHistory: number = 10
): UseUndoRedoReturn<T> {
  const [past, setPast] = useState<T[]>([])
  const [present, setPresent] = useState<T>(initialState)
  const [future, setFuture] = useState<T[]>([])

  const set = useCallback((newState: T | ((prev: T) => T)) => {
    const resolved = typeof newState === 'function' ? (newState as (prev: T) => T)(present) : newState
    
    setPast(prev => {
      const newPast = [...prev, present]
      // Trim history if exceeds max
      if (newPast.length > maxHistory) {
        return newPast.slice(newPast.length - maxHistory)
      }
      return newPast
    })
    setPresent(resolved)
    setFuture([]) // Clear future on new state
  }, [present, maxHistory])

  const undo = useCallback(() => {
    if (past.length === 0) return

    const previous = past[past.length - 1]
    const newPast = past.slice(0, -1)

    setPast(newPast)
    setPresent(previous)
    setFuture(prev => [present, ...prev])
  }, [past, present])

  const redo = useCallback(() => {
    if (future.length === 0) return

    const next = future[0]
    const newFuture = future.slice(1)

    setPast(prev => [...prev, present])
    setPresent(next)
    setFuture(newFuture)
  }, [future, present])

  const reset = useCallback((newState: T) => {
    setPast([])
    setPresent(newState)
    setFuture([])
  }, [])

  return {
    state: present,
    set,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    reset
  }
}
